import { InlineEditableField } from 'app/components/Common/EditPage/InlineEditableField';
import { Controller, useFormContext } from 'react-hook-form';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { pageAttributesActions } from '../store/placementPageAttributes.redux';
import { selectEditStatus, selectPageAttributes } from '../store/placementPageAttributes.selector';
import { editableFields } from '../../editUtils';
import { ActionAccessorHook } from './ActionAccessorHook';
import { CustomNumberField } from '../CustomComponents/CustomNumberField';
import moment from 'moment';
import { getExtensionEndDate } from '../helper';

export const PlacementLength = ({ id }: { id: string }) => {
  const dispatch = useDispatch();
  const { control, setValue, getValues, clearErrors } = useFormContext();

  const value = getValues('placementDate');
  const startDateValue = getValues('placementStartDate');
  const endDateValue = getValues('placementEndDate');
  const placementLength = getValues('numberOfWeeks');

  const { placementDate: status } = useSelector(selectEditStatus);
  const { editOrderStack } = useSelector(selectPageAttributes);
  const undoChecker = () => !editOrderStack.some(key => key === 'numberOfWeeks');
  const endDateMoment = moment(endDateValue);
  const startDateMoment = moment(startDateValue);
  const isValidValue = /^[0-9]{1,4}$/;

  const calculatePlacementDates = (weeksGap: number) => {
    if (startDateMoment?.isValid()) {
      const endDate = getExtensionEndDate(startDateMoment, weeksGap ?? 0);
      const placementDateValue = {
        startDate: startDateMoment?.format('YYYY-MM-DDTHH:mm:ss'),
        endDate:
          weeksGap === 0 ? startDateMoment?.format('YYYY-MM-DDTHH:mm:ss') : endDate?.format('YYYY-MM-DDTHH:mm:ss'),
      };
      setValue('placementEndDate', placementDateValue?.endDate);
      setValue('placementDate', placementDateValue);
      clearErrors('placementEndDate');
    } else if (endDateMoment?.isValid()) {
      const startDate = startDateMoment?.subtract(weeksGap, 'weeks');
      setValue('placementStartDate', startDate?.format('YYYY-MM-DDTHH:mm:ss'));
      const placementDateValue = {
        startDate: startDate?.format('YYYY-MM-DDTHH:mm:ss'),
        endDate: endDateMoment?.format('YYYY-MM-DDTHH:mm:ss'),
      };
      setValue('placementDate', placementDateValue);
    } else {
      const placementDate = {
        ...value,
        startDate: startDateMoment?.isValid() ? startDateMoment?.format('YYYY-MM-DDTHH:mm:ss') : null,
        endDate: endDateMoment?.isValid() ? endDateMoment?.format('YYYY-MM-DDTHH:mm:ss') : null,
      };
      setValue('placementDate', placementDate);
    }
  };

  const handleKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (!/^\d$/.test(event.key)) {
      event.preventDefault();
    }
  };

  return (
    <div>
      <Controller
        control={control}
        name={'numberOfWeeks'}
        render={({ field: { onChange, ...rest } }) => {
          return (
            <>
              {/* * Component is used to access inaccessible methods from react-hook-form Controller
               * to invoke actions - say undo action */}
              <ActionAccessorHook
                actionChecker={undoChecker}
                accessAction={() => onChange(placementLength)}
                hookDependancies={[editOrderStack]}
              />
              <InlineEditableField
                {...rest}
                id={id}
                label={placementLength != null && placementLength >= 0 ? placementLength?.toString() : undefined}
                inputProps={{
                  value: placementLength,
                  minWidth: 80,
                  maxWidth: 220,
                  minValue: 0,
                  maxValue: 9999,
                  onKeyPress: handleKeyPress,
                }}
                Control={CustomNumberField}
                onChange={newValue => {
                  if (newValue !== null && isValidValue.test(newValue?.toString())) {
                    const integerValue = Math.floor(newValue);
                    onChange(integerValue);
                    calculatePlacementDates(integerValue);
                  } else {
                    calculatePlacementDates(0);
                    setValue('numberOfWeeks', null);
                  }
                  dispatch(pageAttributesActions.pushToEditOrderStack(editableFields.numberOfWeeks));
                }}
                setCurrentFocus={state => dispatch(pageAttributesActions.setCurrentFocus(state !== null ? id : null))}
                overflowTooltip={true}
                disabled={startDateValue === null || status.disabled}
              />
            </>
          );
        }}
      />
    </div>
  );
};
